import CardHeader from "components/CardHeader";
import { Tenant, TenantURLs } from "models/Tenant";
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, Input, Label, Spinner } from "reactstrap";
import { updateTenantURLs } from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const defaults = new TenantURLs();

const TenantURLSettings: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
    const { t } = useOfficeTranslation();
    const [account, setAccount] = useState(tenant.urls?.account || defaults.account);
    const [payment, setPayment] = useState(tenant.urls?.payment ?? defaults.payment);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ color: string; text: string } | null>(null);

    useEffect(() => {
        setAccount(tenant.urls?.account || defaults.account);
        setPayment(tenant.urls?.payment ?? defaults.payment);
    }, [tenant]);

    const save = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const saved = await updateTenantURLs(tenant.id, { account, payment });
            setAccount(saved.account);
            setPayment(saved.payment);
            setMessage({ color: "success", text: t("tenants.tenantURLSettings.saved") });
        } catch (reason: any) {
            setMessage({
                color: "danger",
                text: reason.response?.data?.error ?? t("tenants.tenantURLSettings.saveFailed"),
            });
        } finally {
            setSaving(false);
        }
    };

    return <Card>
        <CardBody>
            <CardHeader title={t("tenants.tenantURLSettings.title")} icon="mdi-link-variant" />
            <div className="pt-3">
                {message && <Alert color={message.color}>{message.text}</Alert>}
                <p className="text-muted">{t("tenants.tenantURLSettings.help")}</p>
                <div className="mb-3">
                    <Label for="tenant-account-url">{t("tenants.tenantURLSettings.account")}</Label>
                    <Input
                        autoComplete="url"
                        id="tenant-account-url"
                        maxLength={2048}
                        onChange={event => setAccount(event.target.value)}
                        type="url"
                        value={account}
                    />
                </div>
                <div>
                    <Label for="tenant-payment-url">{t("tenants.tenantURLSettings.payment")}</Label>
                    <Input
                        autoComplete="url"
                        id="tenant-payment-url"
                        maxLength={2048}
                        onChange={event => setPayment(event.target.value)}
                        type="url"
                        value={payment}
                    />
                </div>
                <small className="text-muted d-block mt-2">{t("tenants.tenantURLSettings.defaultHelp")}</small>
                <div className="d-flex justify-content-end mt-3">
                    <Button color="primary" disabled={saving} onClick={save} type="button">
                        {saving ? <Spinner size="sm" /> : t("common.save")}
                    </Button>
                </div>
            </div>
        </CardBody>
    </Card>;
};

export default TenantURLSettings;
